import { Box, Stack, Typography } from '@mui/material';
import {
  SignedRecordJsonObject,
  SignedRecordJsonValue,
  SignedSafetyControlRecord,
} from '../../services/reportsApi';

type TimingBand = {
  label: string;
  context: string | null;
  count: number | null;
  hours: number | null;
  rate: number;
};

function asObject(value: SignedRecordJsonValue | undefined): SignedRecordJsonObject {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as SignedRecordJsonObject
    : {};
}

function firstText(source: SignedRecordJsonObject, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function firstNumber(source: SignedRecordJsonObject, keys: string[]): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return value;
  }
  return null;
}

function timingBands(record: SignedSafetyControlRecord): TimingBand[] {
  const rows = record.signed_body.finding.time_bands;

  return rows.flatMap((value) => {
    const row = asObject(value);
    const label = firstText(row, ['window_label']);
    const count = firstNumber(row, ['reviewed_episodes']);
    const hours = firstNumber(row, ['usable_hours']);
    const rate = firstNumber(row, ['rate_per_usable_hour']);
    if (!label || rate == null) return [];
    return [{
      label,
      context: firstText(row, ['context']),
      count,
      hours,
      rate,
    }];
  });
}

function persistedText(value: SignedRecordJsonValue): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function formatRate(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function HotspotTimeConcentration({
  record,
}: {
  record: SignedSafetyControlRecord;
}) {
  const bands = timingBands(record);
  const finding = record.signed_body.finding;
  const peakWindow = persistedText(finding.peak_window);
  const peakLocation = persistedText(finding.peak_location);
  const peakCounts = persistedText(finding.peak_counts);
  const hasPeakFacts = Boolean(peakWindow || peakLocation || peakCounts);
  const maxRate = Math.max(...bands.map((band) => band.rate), 0) || 1;

  if (!bands.length && !hasPeakFacts) {
    return (
      <Box
        component="section"
        aria-labelledby="time-concentration-heading"
        data-testid="time-concentration-unavailable"
        sx={{ py: 2.5, borderTop: '1px solid rgba(8,27,51,0.18)' }}
      >
        <Typography
          id="time-concentration-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 800 }}
        >
          Time concentration not available on this signed revision
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-labelledby="time-concentration-heading"
      data-testid={bands.length ? 'time-concentration' : 'time-concentration-peak-only'}
      sx={{
        py: { xs: 2.5, md: 3.5 },
        borderTop: '1px solid rgba(8,27,51,0.18)',
        borderBottom: '1px solid rgba(8,27,51,0.18)',
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: '#087fa5', letterSpacing: '0.16em', fontWeight: 800 }}
      >
        Recurring pattern
      </Typography>
      <Typography
        id="time-concentration-heading"
        component="h2"
        sx={{ mt: 0.4, fontSize: { xs: '1.65rem', md: '2.2rem' }, fontWeight: 800 }}
      >
        {bands.length
          ? 'Measured exposure by operating window'
          : 'Peak location and period retained on this signed revision'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          mt: 2.5,
          borderTop: '1px solid rgba(8,27,51,0.18)',
          borderBottom: '1px solid rgba(8,27,51,0.18)',
        }}
      >
        {[
          ['Where', peakLocation || 'Not retained'],
          ['When', peakWindow || 'Not retained'],
          ['Peak', peakCounts || 'Not retained'],
        ].map(([label, value], index) => (
          <Box
            key={label}
            sx={{
              py: 1.75,
              px: { md: 2 },
              borderLeft: { md: index ? '1px solid rgba(8,27,51,0.15)' : 0 },
              borderBottom: { xs: index < 2 ? '1px solid rgba(8,27,51,0.15)' : 0, md: 0 },
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: '0.12em' }}>
              {label.toUpperCase()}
            </Typography>
            <Typography sx={{ mt: 0.45, fontWeight: 700 }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      {bands.length ? (
        <>
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 2.5, mb: 1.25, fontWeight: 900, letterSpacing: '0.1em' }}
          >
            TIME CONCENTRATION · REVIEWED OBSERVATIONS PER USABLE HOUR
          </Typography>
          <Stack spacing={1.4}>
            {bands.map((band, index) => {
              const peakBand = peakWindow != null && band.label === peakWindow;
              return (
                <Box
                  key={`${band.label}-${index}`}
                  data-testid={peakBand ? 'time-concentration-peak' : 'time-concentration-band'}
                  aria-label={`${band.label}: ${formatRate(band.rate)} reviewed observations per usable hour${peakBand ? ', peak' : ''}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '150px minmax(120px, 1fr) 170px' },
                    gap: { xs: 0.6, sm: 1.5 },
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={peakBand ? 900 : 700}>
                      {band.label}
                    </Typography>
                    {band.context ? (
                      <Typography variant="caption" color="text.secondary">{band.context}</Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ height: 18, bgcolor: '#e9f4f8', overflow: 'hidden' }}>
                    <Box
                      data-testid={peakBand ? 'time-concentration-peak-bar' : undefined}
                      sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: peakBand ? '#087fa5' : '#bfe3ef',
                        transform: `scaleX(${Math.min(1, band.rate / maxRate)})`,
                        transformOrigin: 'left center',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', textAlign: { sm: 'right' } }}
                  >
                    {band.count != null && band.hours != null
                      ? `${band.count} episodes / ${band.hours} h · `
                      : ''}
                    <strong>{formatRate(band.rate)}/h</strong>
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Claimable time-band rates were not retained because the measurement denominator
          is not trusted on this signed revision.
        </Typography>
      )}

      {peakCounts && peakWindow ? (
        <Typography data-testid="time-concentration-interpretation" sx={{ mt: 2.5 }}>
          {peakCounts} during {peakWindow}.
        </Typography>
      ) : null}
    </Box>
  );
}
